import type React from 'react';
import type { ValueGetterParams } from 'ag-grid-community';
import 'styles/ag-grid.css';
import { ScheduleOfValue } from 'components/features/schedule-of-values/schedule-of-values-table/types';
import {
  calculatePdValue,
  calculateTotalInsurableValue,
  convertScheduleOfValuesToPayload,
  newRowData,
  validateScheduleOfValue,
} from 'components/features/schedule-of-values/schedule-of-values-table/forms';

import { useTranslation } from 'react-i18next';
import ExcelTable from 'components/ui/excel-table';
import {
  AllRowsData,
  ECellValueType,
  EExcelTableColumnType,
  EExcelTablePaginationType,
  ExcelTableColDef,
  RowData,
} from 'components/ui/excel-table/types';
import { useCallback, useMemo } from 'react';
import { isNumber } from 'lodash';
import { useMutation, useQuery } from 'react-query';
import {
  countryStateCityApi,
  referenceDataApi,
  scheduleOfValuesApi,
} from 'services/apis';
import { handleError } from 'utils/form';
import { EQueryKeys } from 'constants/query-keys';
import {
  SubmissionResponseDtoStatusEnum,
  UpsertScheduleOfValuesPayloadDto,
} from 'libs/pinnacle-sdk';
import { isSubmissionEditable } from 'utils/submission';
import { SOV_PAGE_SIZE_OPTIONS } from 'components/ui/excel-table/constants';

const COUNTRY_NAME_MAPPING: Record<string, string> = {
  'Fiji Islands': 'Fiji',
};

const ScheduleOfValuesTable: React.FC<{
  submissionId: string;
  submissionStatus: SubmissionResponseDtoStatusEnum;
  fileId?: string;
  onErrorChange?: (hasError: boolean) => void;
  scheduleOfValues?: ScheduleOfValue[];
  isLoadingScheduleOfValues: boolean;
  currentPage: number;
  totalRows: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (_: number, size: number) => void;
}> = ({
  onErrorChange,
  scheduleOfValues,
  isLoadingScheduleOfValues,
  currentPage,
  totalRows,
  submissionStatus,
  pageSize,
  onPageChange,
  onPageSizeChange,
  submissionId,
  fileId,
}) => {
  const { t } = useTranslation();
  const isEditingMode = useMemo(() => {
    return isSubmissionEditable(submissionStatus);
  }, [submissionStatus]);

  const { data: countries, isLoading: isLoadingCountries } = useQuery(
    EQueryKeys.COUNTRIES,
    async () => {
      return countryStateCityApi.getCountries();
    },
    {
      refetchOnWindowFocus: false,
      staleTime: Infinity,
    }
  );

  const { data: crestaZones, isLoading: isLoadingCrestaZones } = useQuery(
    EQueryKeys.SOV_CRESTA_ZONES,
    async () => {
      return referenceDataApi.getCrestaZones();
    },
    {
      refetchOnWindowFocus: false,
      staleTime: Infinity,
    }
  );

  const { data: occupancies, isLoading: isLoadingOccupancies } = useQuery(
    EQueryKeys.SOV_OCCUPANCIES,
    async () => {
      return referenceDataApi.getOccupancies();
    },
    {
      refetchOnWindowFocus: false,
      staleTime: Infinity,
    }
  );

  const { data: allStates, isLoading: isLoadingAllStates } = useQuery(
    EQueryKeys.ALL_STATES,
    async () => {
      return countryStateCityApi.getStates();
    },
    {
      refetchOnWindowFocus: false,
      staleTime: Infinity,
    }
  );

  const columnDefs: ExcelTableColDef[] = [
    {
      field: 'country',
      headerName: t('general.country'),
      minWidth: 180,
      flex: 1,
      resizable: true,
      columnType: EExcelTableColumnType.SELECTION,
      selectionOptions:
        countries?.map((country) => ({
          value: COUNTRY_NAME_MAPPING[country.name] || country.name,
          label: COUNTRY_NAME_MAPPING[country.name] || country.name,
        })) || [],
      editable: isEditingMode,
      valueSetter: (params) => {
        if (params.oldValue === params.newValue) {
          return false;
        }
        params.data.country = params.newValue;
        const crestaZoneBelongsToCountry = crestaZones?.some(
          (zone) =>
            zone.name === params.data.crestaZone &&
            zone.country === params.data.country
        );
        if (!crestaZoneBelongsToCountry) {
          params.data.crestaZone = null;
        }
        return true;
      },
    },
    {
      field: 'crestaZone',
      headerName: t('scheduleOfValues.crestaZone'),
      minWidth: 250,
      flex: 1,
      resizable: true,
      columnType: EExcelTableColumnType.SELECTION,
      selectionOptions: (data) => {
        const filteredCrestaZones = crestaZones?.filter(
          (zone) => !data.country || zone.country === data.country
        );
        return (
          filteredCrestaZones?.map((zone) => ({
            value: zone.name,
            label: zone.name,
          })) || []
        );
      },
      editable: isEditingMode,
      valueSetter: (params) => {
        if (params.oldValue === params.newValue) {
          return false;
        }
        params.data.crestaZone = params.newValue;
        const selectedZone = crestaZones?.find(
          (zone) => zone.name === params.newValue
        );
        if (selectedZone) {
          params.data.country = selectedZone.country;
        }
        return true;
      },
    },
    {
      field: 'occupancy',
      headerName: t('scheduleOfValues.occupancy'),
      minWidth: 350,
      flex: 1,
      resizable: true,
      columnType: EExcelTableColumnType.SELECTION,
      selectionOptions:
        occupancies?.map((occupancy) => ({
          value: occupancy.name,
          label: occupancy.name,
        })) || [],
      editable: isEditingMode,
    },
    {
      field: 'buildingValue',
      headerName: t('scheduleOfValues.buildingValue'),
      minWidth: 150,
      type: ECellValueType.NUMERIC_COLUMN,
      flex: 1,
      resizable: true,
      editable: isEditingMode,
    },
    {
      field: 'cpe',
      headerName: t('scheduleOfValues.cpe'),
      minWidth: 150,
      type: ECellValueType.NUMERIC_COLUMN,
      flex: 1,
      resizable: true,
      editable: isEditingMode,
    },
    {
      field: 'stock',
      headerName: t('scheduleOfValues.stock'),
      minWidth: 150,
      type: ECellValueType.NUMERIC_COLUMN,
      flex: 1,
      resizable: true,
      editable: isEditingMode,
    },
    {
      field: 'pdValue',
      headerName: t('scheduleOfValues.pdValue'),
      minWidth: 150,
      type: ECellValueType.NUMERIC_COLUMN,
      flex: 1,
      resizable: true,
      editable: false,
      valueGetter: (params: ValueGetterParams) =>
        isNumber(params.data.locationNumber)
          ? calculatePdValue(params.data)
          : null,
    },
    {
      field: 'biValue',
      headerName: t('scheduleOfValues.biValue'),
      minWidth: 150,
      type: ECellValueType.NUMERIC_COLUMN,
      flex: 1,
      resizable: true,
      editable: isEditingMode,
    },
    {
      field: 'property',
      headerName: t('scheduleOfValues.property'),
      minWidth: 200,
      flex: 1,
      resizable: true,
      editable: isEditingMode,
    },
    {
      field: 'streetAddress',
      headerName: t('scheduleOfValues.streetAddress'),
      minWidth: 250,
      flex: 1,
      resizable: true,
      editable: isEditingMode,
    },
    {
      field: 'suburb',
      headerName: t('scheduleOfValues.suburb'),
      minWidth: 200,
      flex: 1,
      resizable: true,
      editable: isEditingMode,
    },
    {
      field: 'postalZipCode',
      headerName: t('scheduleOfValues.postalZipCode'),
      minWidth: 150,
      flex: 1,
      resizable: true,
      editable: isEditingMode,
    },
    {
      field: 'state',
      headerName: t('general.state'),
      minWidth: 180,
      flex: 1,
      resizable: true,
      columnType: EExcelTableColumnType.SELECTION,
      selectionOptions: (data) => {
        if (!data.country) return [];
        const countryIso = countries?.find(
          (country) => country.name === data.country
        )?.iso;
        if (!countryIso) return [];
        const states = allStates?.filter(
          (state) => state.countryCode === countryIso
        );
        return (
          states?.map((state) => ({
            value: state.name,
            label: state.name,
          })) || []
        );
      },
      editable: isEditingMode,
    },
    {
      field: 'pdDeductibles',
      headerName: t('scheduleOfValues.pdDeductibles'),
      minWidth: 150,
      type: ECellValueType.NUMERIC_COLUMN,
      flex: 1,
      resizable: true,
      editable: isEditingMode,
    },
    {
      field: 'totalInsurableValue',
      headerName: t('scheduleOfValues.totalInsurableValue'),
      minWidth: 250,
      type: ECellValueType.NUMERIC_COLUMN,
      editable: false,
      flex: 1,
      resizable: true,
      valueGetter: (params: ValueGetterParams) =>
        isNumber(params.data.locationNumber)
          ? calculateTotalInsurableValue(params.data)
          : null,
      precision: 0,
    },
    {
      field: 'latitude',
      headerName: t('scheduleOfValues.latitude'),
      minWidth: 150,
      type: ECellValueType.NUMERIC_COLUMN,
      precision: 5,
      flex: 1,
      resizable: true,
      editable: isEditingMode,
    },
    {
      field: 'longitude',
      headerName: t('scheduleOfValues.longitude'),
      minWidth: 150,
      precision: 5,
      type: ECellValueType.NUMERIC_COLUMN,
      flex: 1,
      resizable: true,
      editable: isEditingMode,
    },
    {
      field: 'constructionClass',
      headerName: 'Construction Class',
      minWidth: 200,
      flex: 1,
      resizable: true,
      editable: isEditingMode,
    },
    {
      field: 'roofType',
      headerName: t('scheduleOfValues.roofType'),
      minWidth: 200,
      flex: 1,
      resizable: true,
      editable: isEditingMode,
    },
    {
      field: 'numberOfStories',
      headerName: t('scheduleOfValues.numberOfStories'),
      minWidth: 150,
      type: ECellValueType.NUMERIC_COLUMN,
      flex: 1,
      resizable: true,
      editable: isEditingMode,
      precision: 0,
    },
    {
      field: 'yearBuilt',
      headerName: t('scheduleOfValues.yearBuilt'),
      minWidth: 150,
      flex: 1,
      resizable: true,
      editable: isEditingMode,
    },
    {
      field: 'fluvialFlood',
      headerName: t('scheduleOfValues.fluvialFlood'),
      minWidth: 200,
      flex: 1,
      resizable: true,
      editable: isEditingMode,
    },
    {
      field: 'originalStormSurge',
      headerName: t('scheduleOfValues.originalStormSurge'),
      minWidth: 200,
      flex: 1,
      resizable: true,
      editable: isEditingMode,
    },
    {
      field: 'originalPfRiskLevel',
      headerName: t('scheduleOfValues.originalPfRiskLevel'),
      minWidth: 200,
      flex: 1,
      resizable: true,
      editable: isEditingMode,
    },
    {
      field: 'windstorm',
      headerName: t('scheduleOfValues.windstorm'),
      minWidth: 200,
      flex: 1,
      resizable: true,
      editable: isEditingMode,
    },
    {
      field: 'hailstorm',
      headerName: t('scheduleOfValues.hailstorm'),
      minWidth: 200,
      flex: 1,
      resizable: true,
      editable: isEditingMode,
    },
    {
      field: 'wildfire',
      headerName: t('scheduleOfValues.wildfire'),
      minWidth: 200,
      flex: 1,
      resizable: true,
      editable: isEditingMode,
    },
  ];

  const deleteScheduleOfValuesMutation = useMutation(
    async (sovId: string) => {
      await scheduleOfValuesApi.deleteScheduleOfValues({
        sovId,
      });
    },
    {
      onError: async (error: { response: Response }) => {
        handleError({
          error,
          defaultMessage: t('scheduleOfValues.deleteRecordFailed'),
        });
      },
    }
  );

  const onDeleteRow = useCallback(
    async (scheduleOfValues: RowData<ScheduleOfValue>) => {
      if (scheduleOfValues.data.sovId) {
        await deleteScheduleOfValuesMutation.mutateAsync(
          scheduleOfValues.data.sovId
        );
      }
    },
    [deleteScheduleOfValuesMutation]
  );

  const rowData: ScheduleOfValue[] = useMemo(() => {
    return scheduleOfValues || [];
  }, [scheduleOfValues]);

  const updateSovSettingsMutation = useMutation(
    async (
      upsertScheduleOfValuesPayloadDto: Array<UpsertScheduleOfValuesPayloadDto>
    ) => {
      await scheduleOfValuesApi.upsertScheduleOfValues({
        upsertScheduleOfValuesPayloadDto: upsertScheduleOfValuesPayloadDto,
      });
    },
    {
      onError: async (error: { response: Response }) => {
        handleError({
          error,
          defaultMessage: t('scheduleOfValues.createdRecordsFailed'),
        });
      },
    }
  );

  const onDataChange = useCallback(
    async (
      affectedRow: RowData<ScheduleOfValue>,
      rows: AllRowsData<ScheduleOfValue>
    ) => {
      if (affectedRow.isError.strict) return;
      const currentScheduleOfValues: UpsertScheduleOfValuesPayloadDto[] =
        convertScheduleOfValuesToPayload(rows.data, submissionId);

      await updateSovSettingsMutation.mutateAsync(currentScheduleOfValues);
    },
    [submissionId, updateSovSettingsMutation]
  );

  return (
    <ExcelTable
      rowKeyColName="locationNumber"
      baseTableHeight={490}
      tableHeightBias={370}
      columnDefs={columnDefs}
      data={rowData}
      tableClassName="rounded-t-lg"
      onErrorChange={({ strict }) => {
        onErrorChange?.(strict);
      }}
      className="mb-4"
      paginationType={EExcelTablePaginationType.DETAIL}
      includeIdRow={true}
      includeAddRow={isEditingMode && totalRows <= currentPage * pageSize}
      includeDeleteCol={isEditingMode}
      newRowData={{ ...newRowData, submissionId, fileId }}
      validateRow={validateScheduleOfValue}
      loading={
        isLoadingScheduleOfValues ||
        isLoadingCountries ||
        isLoadingOccupancies ||
        isLoadingCrestaZones ||
        isLoadingAllStates
      }
      currentPage={currentPage}
      totalRows={totalRows}
      pageSize={pageSize}
      pageSizeOptions={SOV_PAGE_SIZE_OPTIONS}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
      onDeleteRow={isEditingMode ? onDeleteRow : undefined}
      onDataChange={isEditingMode ? onDataChange : undefined}
    />
  );
};

export default ScheduleOfValuesTable;
